import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { getPairs } from '../../api/compatibility';

/**
 * Zwijana lista „Sprawdzone pary według marki falownika” pod narzędziem na /kompatybilnosc.
 *
 * Backend renderuje ok. 250 stron /kompatybilnosc/<slug> (strona na regułę: grupa falowników
 * × rodzina baterii), ale do 2026-10-14 prowadził do nich tylko spis dla botów. Tu użytkownik
 * dostaje te same linki. Dane z `GET /api/v1/compatibility/pairs` (cache po stronie serwera),
 * pobierane dopiero przy pierwszym rozwinięciu. Adresy przychodzą gotowe, w Rumunii z prefiksem
 * /ro, więc lista działa w obu wersjach. Zwykłe <a href>: stronę pary renderuje backend.
 */
const ListaParZgodnosci = () => {
  const { t } = useTranslation('zgodnosc');
  const [otwarta, setOtwarta] = useState(false);
  const [stan, setStan] = useState({ etap: 'brak', items: [] });

  const zamontowana = useRef(true);
  useEffect(() => {
    zamontowana.current = true;
    return () => { zamontowana.current = false; };
  }, []);

  // Pierwsze rozwinięcie (albo ponowne po błędzie) pobiera listę. Zwinięcie w trakcie nie przerywa pobierania.
  const przelacz = () => {
    setOtwarta((v) => !v);
    if (otwarta || stan.etap === 'ok' || stan.etap === 'laduje') return;
    setStan({ etap: 'laduje', items: [] });
    getPairs()
      .then((d) => { if (zamontowana.current) setStan({ etap: 'ok', items: d.items || [] }); })
      .catch(() => { if (zamontowana.current) setStan({ etap: 'blad', items: [] }); });
  };

  // Marki w kolejności z API (alfabetycznie), pary w marce po slugu.
  const grupy = useMemo(() => {
    const m = new Map();
    stan.items.forEach((p) => {
      const marka = p.inverter_manufacturer || '';
      if (!m.has(marka)) m.set(marka, []);
      m.get(marka).push(p);
    });
    return [...m.entries()];
  }, [stan.items]);

  return (
    <section className="mt-10 pt-6 border-t border-gray-100">
      <button
        type="button"
        onClick={przelacz}
        aria-expanded={otwarta}
        className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900"
      >
        <FontAwesomeIcon icon={faChevronDown} className={`text-xs transition-transform ${otwarta ? 'rotate-180' : ''}`} />
        {t('pary.tytul')}
        {stan.etap === 'ok' && <span className="font-normal text-gray-400">{t('pary.liczba', { liczba: stan.items.length })}</span>}
      </button>

      {otwarta && (
        <div className="mt-4">
          {stan.etap === 'laduje' && <div className="h-24 rounded-xl bg-gray-100 animate-pulse" aria-busy="true" aria-label={t('wczytywanie')} />}
          {stan.etap === 'blad' && <p className="text-sm text-rose-600">{t('pary.blad')}</p>}
          {stan.etap === 'ok' && (
            <div className="space-y-5">
              {grupy.map(([marka, pary]) => (
                <div key={marka}>
                  <h3 className="text-sm font-semibold text-gray-900 mb-1.5">
                    {t('pary.falowniki', { marka })}
                    <span className="ml-1.5 font-normal text-gray-400">{pary.length}</span>
                  </h3>
                  <ul className="grid gap-x-6 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
                    {pary.map((p) => (
                      <li key={p.slug} className="min-w-0">
                        <a href={p.url} className="block truncate text-sm text-blue-700 hover:underline" title={p.name}>{p.name}</a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ListaParZgodnosci;
